import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Line } from 'react-chartjs-2';
import { formatDateTimeIST } from '../../utils/dateUtils';
import SetupGuide from './SetupGuide';
import PostmanGuide from './PostmanGuide';

const APIServiceDetails = ({ serviceId, onClose }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showKey, setShowKey] = useState(false);
    const [activityPage, setActivityPage] = useState(1);
    const [activeTab, setActiveTab] = useState('overview');
    const activityPerPage = 10;

    useEffect(() => {
        fetchDetails();
    }, [serviceId]);

    const fetchDetails = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${process.env.REACT_APP_BACKEND_URL}/api/client/api-services/${serviceId}/details`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setDetails(response.data);
        } catch (error) {
            console.error('Error fetching service details:', error);
        } finally {
            setLoading(false);
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        alert('API Key copied to clipboard!');
    };

    if (loading) {
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-8 max-w-4xl w-full mx-4">
                    <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                    </div>
                </div>
            </div>
        );
    }

    if (!details) {
        return null;
    }

    const { consumption } = details;
    const currentMonth = consumption?.current_month || {};
    const lastMonth = consumption?.last_month || {};

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">{details.name}</h2>
                        <p className="text-sm text-gray-500">Service ID: {serviceId}</p>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {/* Tab Navigation */}
                    <div className="flex border-b border-gray-200 overflow-x-auto">
                        <button
                            onClick={() => setActiveTab('overview')}
                            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${activeTab === 'overview'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            📊 Overview
                        </button>
                        <button
                            onClick={() => setActiveTab('guide')}
                            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${activeTab === 'guide'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            📚 Setup Guide
                        </button>
                        <button
                            onClick={() => setActiveTab('postman')}
                            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors whitespace-nowrap ${activeTab === 'postman'
                                ? 'border-indigo-600 text-indigo-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            🧪 Postman Testing
                        </button>
                    </div>

                    {activeTab === 'overview' && (
                        <>
                            {/* API Key Section */}
                            {details.api_key && (
                                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 border border-indigo-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-3">API Key</h3>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type={showKey ? 'text' : 'password'}
                                            value={details.api_key}
                                            readOnly
                                            className="flex-1 px-4 py-2 border border-gray-300 rounded-md bg-white font-mono text-sm"
                                        />
                                        <button
                                            onClick={() => setShowKey(!showKey)}
                                            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                                        >
                                            {showKey ? 'Hide' : 'Show'}
                                        </button>
                                        <button
                                            onClick={() => copyToClipboard(details.api_key)}
                                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                        >
                                            Copy
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Consumption Metrics */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-500">Total Requests</p>
                                    <p className="text-2xl font-bold text-gray-900 mt-1">
                                        {currentMonth.total_requests?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">This month</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-500">Billable Requests</p>
                                    <p className="text-2xl font-bold text-indigo-600 mt-1">
                                        {currentMonth.billable_requests?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">Charged requests</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-500">Documents Consumed</p>
                                    <p className="text-2xl font-bold text-green-600 mt-1">
                                        {currentMonth.documents_consumed?.toLocaleString() || 0}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">Documents delivered</p>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-lg p-4">
                                    <p className="text-sm text-gray-500">Current Cost</p>
                                    <p className="text-2xl font-bold text-orange-600 mt-1">
                                        ₹{currentMonth.cost?.toFixed(2) || '0.00'}
                                    </p>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Last month: ₹{lastMonth.cost?.toFixed(2) || '0.00'}
                                    </p>
                                </div>
                            </div>

                            {/* Recent Activity */}
                            {details.recent_activity && details.recent_activity.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h3>
                                    <div className="overflow-x-auto">
                                        <table className="min-w-full divide-y divide-gray-200">
                                            <thead className="bg-gray-50">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Time
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Endpoint
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Status
                                                    </th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                                        Response Time
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {details.recent_activity
                                                    .slice((activityPage - 1) * activityPerPage, activityPage * activityPerPage)
                                                    .map((log, index) => (
                                                        <tr key={index} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                                {formatDateTimeIST(log.timestamp)}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 font-mono">
                                                                {log.endpoint}
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span
                                                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${log.status === 'success'
                                                                        ? 'bg-green-100 text-green-800'
                                                                        : 'bg-red-100 text-red-800'
                                                                        }`}
                                                                >
                                                                    {log.status}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                                {log.response_time_ms}ms
                                                            </td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Pagination */}
                                    {details.recent_activity.length > activityPerPage && (
                                        <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
                                            <div className="text-sm text-gray-700">
                                                Showing {Math.min((activityPage - 1) * activityPerPage + 1, details.recent_activity.length)} - {Math.min(activityPage * activityPerPage, details.recent_activity.length)} of {details.recent_activity.length} logs
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => setActivityPage(activityPage - 1)}
                                                    disabled={activityPage === 1}
                                                    className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                >
                                                    Previous
                                                </button>
                                                <span className="px-3 py-1 text-sm text-gray-700">
                                                    Page {activityPage} of {Math.ceil(details.recent_activity.length / activityPerPage)}
                                                </span>
                                                <button
                                                    onClick={() => setActivityPage(activityPage + 1)}
                                                    disabled={activityPage >= Math.ceil(details.recent_activity.length / activityPerPage)}
                                                    className="px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                                                >
                                                    Next
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>)}

                    {activeTab === 'guide' && (
                        <SetupGuide serviceId={serviceId} />
                    )}

                    {activeTab === 'postman' && (
                        <PostmanGuide serviceId={serviceId} />
                    )}

                    {/* Close Button */}
                    <div className="flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default APIServiceDetails;
